import { useEffect, useState } from "react";
import { Loader2, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { Loader } from "@/components/common";

export default function IngredientsDialog({ hubId, start, onClose }) {
  const [d, setD] = useState(null);
  const [picked, setPicked] = useState(null);
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/organizations/${hubId}/meals`;
  const listId = picked || d?.list_id || null;
  // Refetches only when the user picks another list; their ticks are kept (first load ticks everything not already on the list).
  useEffect(() => {
    api.get(`${base}/ingredients`, { params: { start, list_id: picked || undefined } }).then(({ data }) => {
      setD(data);
      setOn((cur) => cur || new Set(data.items.filter((i) => !i.on_list && !i.in_pantry).map((i) => i.key)));
    }).catch((e) => toast.error(errMsg(e)));
  }, [base, start, picked]);
  const confirm = async () => {
    setBusy(true);
    try {
      const items = d.items.filter((i) => ticks.has(i.key)).map((i) => ({ item: i.item, qty: i.qty, note: i.note }));
      const { data } = await api.post(`${base}/ingredients`, { list_id: listId, items });
      toast.success(`Added ${data.added} item${data.added === 1 ? "" : "s"} to ${d.lists.find((l) => l.id === listId)?.name}`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const flip = (key) => setOn((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const ticks = on || new Set();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="ingredients-dialog">
        <DialogHeader><DialogTitle className="font-display">Add this week's ingredients</DialogTitle></DialogHeader>
        {!d ? <Loader /> : (
          <div className="space-y-3">
            <label className="block text-xs font-bold text-q-muted">Add to
              <select className="q-input mt-1 w-full" value={listId || ""} onChange={(e) => setPicked(e.target.value)} data-testid="ingredients-list-select">
                {d.lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            {d.empty_message ? <p className="py-6 text-center text-sm text-q-muted" data-testid="ingredients-empty">{d.empty_message}</p> : (
              <ul className="divide-y divide-q-line" data-testid="ingredients-list">
                {d.items.map((i) => (
                  <li key={i.key}>
                    <label className="flex min-h-[52px] cursor-pointer items-center gap-3 py-2" data-testid={`ingredient-row-${i.key.replace(/\s+/g, "-")}`}>
                    <Checkbox className="h-6 w-6" checked={ticks.has(i.key)} onCheckedChange={() => flip(i.key)} data-testid={`ingredient-check-${i.key.replace(/\s+/g, "-")}`} />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-q-text">{i.item}{i.qty && <span className="ml-2 text-sm font-normal text-q-muted">{i.qty}</span>}</p>
                      <p className="truncate text-[11px] text-q-faint">{i.note}</p>
                    </div>
                    {i.in_pantry && <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-1 text-[10px] font-bold text-emerald-300" data-testid={`ingredient-in-pantry-${i.key.replace(/\s+/g, "-")}`}>In pantry</span>}
                    {i.on_list && <span className="shrink-0 rounded-full bg-q-raised px-2 py-1 text-[10px] font-bold text-q-muted" data-testid="ingredient-on-list">already on list</span>}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <button className="q-btn q-btn-primary w-full" disabled={busy || !!d.empty_message || !ticks.size || !listId} onClick={confirm} data-testid="ingredients-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBasket className="h-4 w-4" />} Confirm
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
